import { useCallback, useEffect } from 'react';
import type { EnumCategory } from '../types';
import { useSearch } from './useSearch';

export const useKeyboardNavigation = () => {
  const { category, sidebar, resultsByCategory, selectedItem, setSelectedItem, isOpen, setIsOpen, onChangeCategory } = useSearch();

  const handleResultNavigation = useCallback(
    (direction: 'up' | 'down') => {
      if (resultsByCategory.length) {
        const currentIndex = selectedItem ? resultsByCategory.findIndex((item) => item.id === selectedItem.id) : -1;
        const nextIndex = direction === 'down' ? (currentIndex + 1) % resultsByCategory.length : currentIndex <= 0 ? resultsByCategory.length - 1 : currentIndex - 1;
        setSelectedItem(resultsByCategory[nextIndex]);
      }
    },
    [resultsByCategory, selectedItem, setSelectedItem, category.id]
  );

  const handleCategoryNavigation = useCallback(
    (direction: 'up' | 'down') => {
      const allCategories = sidebar.flatMap((group) => group.categories);
      const currentIndex = allCategories.findIndex((cat) => cat.id === category.id);
      const nextIndex = direction === 'down' ? (currentIndex + 1) % allCategories.length : currentIndex <= 0 ? allCategories.length - 1 : currentIndex - 1;

      onChangeCategory({
        categoryId: allCategories[nextIndex].id as EnumCategory,
        keepSearch: true,
      });
    },
    [category.id, onChangeCategory, sidebar]
  );

  const handleQuickCategorySelection = useCallback(
    (numberKey: number) => {
      const allCategories = sidebar.flatMap((group) => group.categories);
      const index = numberKey - 1;

      if (index < allCategories.length) {
        onChangeCategory({
          categoryId: allCategories[index].id as EnumCategory,
          keepSearch: true,
        });
      }
    },
    [onChangeCategory, sidebar]
  );

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Ignore if target is an input
      if (e.target instanceof HTMLInputElement) return;

      if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setIsOpen(!isOpen);
      }

      // Navigation through results with arrow keys
      if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
        e.preventDefault();

        if (e.altKey || e.metaKey) {
          // Category navigation with Alt + Arrow keys
          handleCategoryNavigation(e.key === 'ArrowDown' ? 'down' : 'up');
        } else {
          // Results navigation with Arrow keys
          handleResultNavigation(e.key === 'ArrowDown' ? 'down' : 'up');
        }
      }

      // Quick category selection with Alt/Command + number (1-9)
      if ((e.altKey || e.metaKey) && /^[1-9]$/.test(e.key)) {
        e.preventDefault();
        handleQuickCategorySelection(Number.parseInt(e.key));
      }

      // Focus on search input
      if ((e.altKey || e.metaKey) && e.key === 'p') {
        e.preventDefault();
        const searchInput = document.querySelector('input[cmdk-input]') as HTMLInputElement;
        if (searchInput) searchInput.focus();
      }

      // Close with Escape
      if (e.key === 'Escape') {
        e.preventDefault();
        setIsOpen(false);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleResultNavigation, handleCategoryNavigation, handleQuickCategorySelection, selectedItem, setIsOpen, setSelectedItem]);
};
